import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/lib/jwt-auth-client";

const MAX_VOICE_RECORDING_MS = 55_000;

function isFramedContext(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

async function ensureMicrophoneAccess(): Promise<{
  ok: boolean;
  stream?: MediaStream;
  message?: string;
}> {
  if (typeof window === "undefined") {
    return { ok: false, message: "Microphone is only available in the browser." };
  }
  if (!window.isSecureContext) {
    return {
      ok: false,
      message: `Mic blocked on insecure origin (${window.location.origin}). Use HTTPS or localhost.`,
    };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, message: "Browser does not expose microphone APIs in this context." };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    return { ok: true, stream };
  } catch (error) {
    const err = error as DOMException | Error;
    const name = err?.name || "UnknownError";
    const framed = isFramedContext();
    if (name === "NotAllowedError" || name === "SecurityError") {
      return {
        ok: false,
        message: framed
          ? 'Mic blocked by browser or iframe policy. If embedded, set iframe allow="microphone" and permit microphone for this origin.'
          : "Mic permission denied. Allow microphone in browser site settings, then reload and retry.",
      };
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      return { ok: false, message: "No microphone device found. Connect a mic and retry." };
    }
    if (name === "NotReadableError" || name === "TrackStartError") {
      return {
        ok: false,
        message: "Microphone is busy or unavailable (possibly in use by another app).",
      };
    }
    return {
      ok: false,
      message: `Microphone unavailable (${name}). Check browser and OS privacy settings.`,
    };
  }
}

function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return undefined;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

function extensionForMimeType(mimeType: string): string {
  const normalized = mimeType.split(";")[0].trim().toLowerCase();
  if (normalized === "audio/ogg") return "ogg";
  if (normalized === "audio/mp4") return "m4a";
  if (normalized === "audio/mpeg") return "mp3";
  if (normalized === "audio/wav") return "wav";
  return "webm";
}

interface UseSpeechRecognitionOptions {
  onTranscriptReady: (text: string) => void;
  interruptAssistantPlayback: (reason: string) => void;
}

export function useSpeechRecognition({
  onTranscriptReady,
  interruptAssistantPlayback,
}: UseSpeechRecognitionOptions) {
  const [isListening, setIsListening] = useState(false);
  const [micHint, setMicHint] = useState<string | null>(null);
  const [isSecureMicContext, setIsSecureMicContext] = useState(true);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const isListeningRef = useRef(false);
  const shouldSubmitOnStopRef = useRef(false);
  const isTranscribingRef = useRef(false);
  const recordingStartedAtRef = useRef(0);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const voiceTranscriptRef = useRef("");
  const liveDisplayRef = useRef("");

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const resetRecordingState = useCallback(() => {
    clearTimer();
    chunksRef.current = [];
    isListeningRef.current = false;
    shouldSubmitOnStopRef.current = false;
    recordingStartedAtRef.current = 0;
    liveDisplayRef.current = "";
    recorderRef.current = null;
    setIsListening(false);
    setIsProcessingVoice(false);
    stopTracks();
  }, [clearTimer, stopTracks]);

  const transcribeAudio = useCallback(
    async (blob: Blob, durationMs?: number) => {
      if (blob.size < 800) {
        setMicHint("No voice detected. Try again a little closer to the mic.");
        return;
      }
      if (isTranscribingRef.current) return;

      isTranscribingRef.current = true;
      setIsProcessingVoice(true);
      setMicHint(null);
      try {
        const mimeType = blob.type || "audio/webm";
        const form = new FormData();
        form.append("audio", blob, `voice-message.${extensionForMimeType(mimeType)}`);
        if (typeof durationMs === "number") {
          form.append("durationMs", String(Math.max(0, Math.round(durationMs))));
        }
        const res = await apiFetch("/core/agent/voice/transcribe", {
          method: "POST",
          body: form,
        });
        const payload = (await res.json().catch(() => ({}))) as { text?: string; error?: string };
        if (!res.ok) {
          throw new Error(payload.error || `Voice transcription failed (${res.status})`);
        }
        const text = payload.text?.trim() ?? "";
        if (!text) {
          setMicHint("No speech was detected. Try again.");
          return;
        }
        voiceTranscriptRef.current = text;
        liveDisplayRef.current = text;
        setMicHint(null);
        onTranscriptReady(text);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Voice transcription failed.";
        setMicHint(message);
      } finally {
        isTranscribingRef.current = false;
        setIsProcessingVoice(false);
      }
    },
    [onTranscriptReady],
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const secure = window.isSecureContext;
      setIsSecureMicContext(secure);
      if (!secure) {
        setMicHint(
          `Mic unavailable on insecure origin (${window.location.origin}). Use HTTPS or localhost.`,
        );
      }
    }

    if (typeof MediaRecorder === "undefined") {
      setMicHint("Voice recording is not supported in this browser.");
    }

    return () => {
      shouldSubmitOnStopRef.current = false;
      try {
        recorderRef.current?.stop();
      } catch {
        // Ignore stop errors during teardown.
      }
      clearTimer();
      chunksRef.current = [];
      isListeningRef.current = false;
      setIsProcessingVoice(false);
      recordingStartedAtRef.current = 0;
      liveDisplayRef.current = "";
      stopTracks();
    };
  }, [clearTimer, stopTracks]);

  const startRecording = useCallback(async () => {
    if (typeof window !== "undefined" && !window.isSecureContext) {
      const message = `Mic blocked on insecure origin (${window.location.origin}). Use HTTPS or localhost.`;
      setMicHint(message);
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setMicHint("Voice recording is not supported in this browser.");
      return;
    }
    if (isTranscribingRef.current) return;

    const micAccess = await ensureMicrophoneAccess();
    if (!micAccess.ok || !micAccess.stream) {
      const message = micAccess.message || "Mic permission denied or unavailable.";
      setMicHint(message);
      return;
    }

    const mimeType = pickRecorderMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(
        micAccess.stream,
        mimeType ? { mimeType } : undefined,
      );
    } catch (error) {
      micAccess.stream.getTracks().forEach((track) => track.stop());
      const message = error instanceof Error ? error.message : "Voice recorder failed to start.";
      setMicHint(message);
      return;
    }
    recorderRef.current = recorder;
    streamRef.current = micAccess.stream;
    chunksRef.current = [];
    voiceTranscriptRef.current = "";
    liveDisplayRef.current = "";
    setMicHint(null);

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      setMicHint("Voice recorder failed. Please retry.");
      shouldSubmitOnStopRef.current = false;
      resetRecordingState();
    };
    recorder.onstop = () => {
      const shouldSubmit = shouldSubmitOnStopRef.current;
      const recordedType = recorder.mimeType || mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: recordedType });
      const durationMs = Date.now() - recordingStartedAtRef.current;
      resetRecordingState();
      if (shouldSubmit) void transcribeAudio(blob, durationMs);
    };

    shouldSubmitOnStopRef.current = true;
    isListeningRef.current = true;
    recordingStartedAtRef.current = Date.now();
    interruptAssistantPlayback("mic-start");
    recorder.start(250);
    setIsListening(true);

    clearTimer();
    recordingTimerRef.current = setInterval(() => {
      const elapsedMs = Date.now() - recordingStartedAtRef.current;
      if (elapsedMs >= MAX_VOICE_RECORDING_MS) {
        shouldSubmitOnStopRef.current = true;
        setMicHint(null);
        try {
          recorderRef.current?.stop();
        } catch {
          resetRecordingState();
        }
        return;
      }
    }, 1000);
  }, [clearTimer, interruptAssistantPlayback, resetRecordingState, transcribeAudio]);

  const toggleListen = useCallback(async () => {
    if (isTranscribingRef.current) return;
    if (isListeningRef.current || isListening) {
      shouldSubmitOnStopRef.current = true;
      setMicHint(null);
      try {
        recorderRef.current?.stop();
      } catch {
        resetRecordingState();
      }
      return;
    }
    await startRecording();
  }, [isListening, resetRecordingState, startRecording]);

  const clearTranscript = useCallback(() => {
    voiceTranscriptRef.current = "";
    liveDisplayRef.current = "";
  }, []);

  const stopListening = useCallback(() => {
    voiceTranscriptRef.current = "";
    liveDisplayRef.current = "";
    shouldSubmitOnStopRef.current = false;
    try {
      recorderRef.current?.stop();
    } catch {
      resetRecordingState();
    }
  }, [resetRecordingState]);

  const setInputFromVoice = useCallback((text: string) => {
    voiceTranscriptRef.current = text;
    liveDisplayRef.current = text;
  }, []);

  return {
    isListening,
    isProcessingVoice,
    micHint,
    isSecureMicContext,
    toggleListen,
    clearTranscript,
    stopListening,
    setInputFromVoice,
    voiceTranscriptRef,
    liveDisplayRef,
  };
}
